import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EMPTY, Subject, catchError, expand, of, startWith, switchMap, takeUntil, tap, timer } from 'rxjs';
import {
  DataFeedEntity,
  DataFeedEvent,
  DataFeedIssue,
  DataFeedIssuePage,
  DataFeedTrace,
} from '../../../models/data-feed.model';
import { EtlMonitorService } from '../../../services/etl-monitor.service';
import { DataFeedRunAction, DataFeedRunControl } from '../data-feed-run-control';
import {
  BadgeMeta,
  describeHttpError,
  entityDurationMs,
  entityProgress,
  entityStatusMeta,
  eventMeta,
  formatDuration,
  genericStatusMeta,
  hasPendingControl,
  PendingControlMeta,
  pendingControlMeta,
  prettyJson,
  safeParse,
  severityMeta,
  stageMeta,
  uploadDurationMs,
} from '../data-feed-ui';

export interface DataFeedTraceDialogData {
  runId: string;
  fileName: string | null;
}

/** Tab indexes (Overview, Entities, Errors and Timeline are always present, in this order). */
const TAB_ERRORS = 2;
const TRACE_POLL_MS = 3000;

@Component({
  selector: 'app-data-feed-trace-dialog',
  templateUrl: './data-feed-trace-dialog.component.html',
  styleUrls: ['./data-feed-trace-dialog.component.scss']
})
export class DataFeedTraceDialogComponent implements OnInit, OnDestroy {
  readonly runId: string;
  readonly fileName: string | null;
  readonly skeletonRows = [1, 2, 3, 4];
  readonly pageSizeOptions = [25, 50, 100];

  trace: DataFeedTrace | null = null;
  loading = true;
  loadError: string | null = null;
  pollError: string | null = null;
  now = Date.now();
  selectedTab = 0;

  // Errors tab
  issueEntity = '';
  issueKind = '';
  issuePageIndex = 0;
  issuePageSize = 50;
  issuePage: DataFeedIssuePage | null = null;
  issuesLoading = false;
  issuesError: string | null = null;
  expandedIssues = new Set<number>();
  private issuesRequested = false;
  private lastIssueTotal = -1;

  // Timeline tab
  problemsOnly = false;
  expandedEvents = new Set<number>();

  private readonly destroy$ = new Subject<void>();
  private readonly reload$ = new Subject<void>();
  private readonly issues$ = new Subject<void>();
  /** Pause / Start / Stop requests (confirm, call, snack bar). */
  readonly runControl: DataFeedRunControl;

  constructor(
    public dialogRef: MatDialogRef<DataFeedTraceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DataFeedTraceDialogData,
    private etlMonitorService: EtlMonitorService,
    private clipboard: Clipboard,
    private snackBar: MatSnackBar,
    dialog: MatDialog
  ) {
    this.runId = data.runId;
    this.fileName = data.fileName;
    this.runControl = new DataFeedRunControl(dialog, snackBar, etlMonitorService);
  }

  ngOnInit(): void {
    // Load the trace; keep polling every 3s while the run is still queued / processing.
    this.reload$.pipe(
      startWith(undefined),
      switchMap(() => {
        const fetch$ = this.etlMonitorService.getDataFeedTrace(this.runId).pipe(
          tap(trace => this.applyTrace(trace))
        );
        return fetch$.pipe(
          expand(trace => this.isRunning(trace)
            ? timer(TRACE_POLL_MS).pipe(switchMap(() => fetch$))
            : EMPTY),
          catchError(err => {
            this.loading = false;
            const message = err?.status === 404
              ? 'This run could not be found. It may have been removed by retention.'
              : describeHttpError(err, 'Could not load the trace');
            if (this.trace) {
              this.pollError = message;
            } else {
              this.loadError = message;
            }
            return of(null);
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    // Issues page loader (latest request wins).
    this.issues$.pipe(
      switchMap(() => {
        this.issuesLoading = true;
        this.issuesError = null;
        return this.etlMonitorService.getDataFeedIssues(this.runId, {
          entity: this.issueEntity || null,
          kind: this.issueKind || null,
          page: this.issuePageIndex + 1,
          pageSize: this.issuePageSize,
        }).pipe(
          catchError(err => {
            this.issuesError = describeHttpError(err, 'Could not load errors');
            return of(null);
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(page => {
      this.issuesLoading = false;
      if (page) {
        this.issuePage = page;
        this.expandedIssues.clear();
      }
    });

    timer(1000, 1000).pipe(takeUntil(this.destroy$)).subscribe(() => this.now = Date.now());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyTrace(trace: DataFeedTrace): void {
    this.trace = trace;
    this.loading = false;
    this.loadError = null;
    this.pollError = null;
    this.now = Date.now();

    // New issues arrived while the run is processing: refresh the visible page.
    const total = this.totalIssues;
    if (this.issuesRequested && this.lastIssueTotal >= 0 && total !== this.lastIssueTotal) {
      this.issues$.next();
    }
    this.lastIssueTotal = total;
  }

  private isRunning(trace: DataFeedTrace | null): boolean {
    const stage = trace?.Upload?.Stage;
    // Keep polling while a stop / resume request waits for the worker, so its outcome shows promptly.
    return stage === 'Queued' || stage === 'Processing' || stage === 'Uploaded' || hasPendingControl(trace?.Upload);
  }

  get running(): boolean {
    return this.isRunning(this.trace);
  }

  retry(): void {
    this.loading = !this.trace;
    this.loadError = null;
    this.pollError = null;
    this.reload$.next();
  }

  close(): void {
    this.dialogRef.close();
  }

  /** Pause, start or stop this run: confirm, send, show the server's message, then reload the trace. */
  controlRun(action: DataFeedRunAction): void {
    const upload = this.trace?.Upload;
    if (!upload) { return; }
    // The trace's upload may not carry the run id when it is keyed by upload; the dialog always has it.
    this.runControl.run(action, { ...upload, RunId: upload.RunId || this.runId })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.reload$.next());
  }

  /** Pending pause / cancel / start chip shown next to the stage badge, or null. */
  get pendingControl(): PendingControlMeta | null {
    return pendingControlMeta(this.trace?.Upload);
  }

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  get entities(): DataFeedEntity[] {
    return [...(this.trace?.Upload?.Entities ?? [])].sort((a, b) => a.Sequence - b.Sequence);
  }

  get totalIssues(): number {
    return (this.trace?.IssueCounts ?? []).reduce((sum, c) => sum + (c.Count || 0), 0);
  }

  get issueEntities(): { name: string; count: number }[] {
    return this.groupCounts(c => c.Entity, this.issueKind ? c => c.Kind === this.issueKind : null);
  }

  get issueKinds(): { name: string; count: number }[] {
    return this.groupCounts(c => c.Kind, this.issueEntity ? c => c.Entity === this.issueEntity : null);
  }

  private groupCounts(
    key: (c: { Entity: string; Kind: string; Count: number }) => string,
    filter: ((c: { Entity: string; Kind: string; Count: number }) => boolean) | null
  ): { name: string; count: number }[] {
    const map = new Map<string, number>();
    for (const c of this.trace?.IssueCounts ?? []) {
      if (filter && !filter(c)) { continue; }
      map.set(key(c), (map.get(key(c)) ?? 0) + (c.Count || 0));
    }
    return Array.from(map.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name));
  }

  get visibleEvents(): DataFeedEvent[] {
    const events = this.trace?.Events ?? [];
    if (!this.problemsOnly) { return events; }
    return events.filter(e => {
      const s = (e.Severity || '').toLowerCase();
      return s === 'warning' || s === 'error';
    });
  }

  get problemEventCount(): number {
    return (this.trace?.Events ?? []).filter(e => /^(warning|error)$/i.test(e.Severity || '')).length;
  }

  get alertText(): string | null {
    const u = this.trace?.Upload;
    if (!u) { return null; }
    return u.FailureReason || u.StageReason || null;
  }

  get alertTone(): string {
    const stage = this.trace?.Upload?.Stage;
    if (stage === 'Failed' || stage === 'Rejected') { return 'failed'; }
    if (stage === 'CompletedWithErrors') { return 'warning'; }
    if (stage === 'Cancelled') { return 'cancelled'; }
    if (stage === 'Paused') { return 'paused'; }
    const pending = this.pendingControl;
    if (pending && pending.tone !== 'processing') { return pending.tone; }
    return 'processing';
  }

  /** Icon for the overview status box, matching its tone. */
  get alertIcon(): string {
    switch (this.alertTone) {
      case 'failed': return 'error';
      case 'warning': return 'warning';
      case 'paused': return 'pause_circle';
      case 'cancelled': return 'cancel';
      default: return 'info';
    }
  }

  // ---------------------------------------------------------------------------
  // Template helpers
  // ---------------------------------------------------------------------------

  stageMeta(): BadgeMeta {
    return stageMeta(this.trace?.Upload?.Stage);
  }

  entityMeta(entity: DataFeedEntity): BadgeMeta {
    return entityStatusMeta(entity.Status);
  }

  statusMeta(status: string | null | undefined): BadgeMeta {
    return genericStatusMeta(status);
  }

  severityMeta(event: DataFeedEvent): BadgeMeta {
    return severityMeta(event.Severity);
  }

  /** Marker icon / tone for a timeline event (event type when known, otherwise severity). */
  eventMeta(event: DataFeedEvent): BadgeMeta {
    return eventMeta(event.EventType, event.Severity);
  }

  runDuration(): string {
    return this.trace ? formatDuration(uploadDurationMs(this.trace.Upload, this.now)) : '-';
  }

  entityDuration(entity: DataFeedEntity): string {
    return formatDuration(entityDurationMs(entity, this.now));
  }

  entityProgress(entity: DataFeedEntity): number | null {
    return entityProgress(entity);
  }

  duration(ms: number | null | undefined): string {
    return formatDuration(ms);
  }

  pretty(text: string | null | undefined): string {
    return prettyJson(text);
  }

  trackByEntity(_: number, e: DataFeedEntity): string { return e.Entity; }
  trackByEvent(_: number, e: DataFeedEvent): number { return e.EventId; }
  trackByIssue(_: number, i: DataFeedIssue): number { return i.IssueId; }
  trackByName(_: number, o: { name: string }): string { return o.name; }

  // ---------------------------------------------------------------------------
  // Errors tab
  // ---------------------------------------------------------------------------

  onTabChange(index: number): void {
    this.selectedTab = index;
    if (index === TAB_ERRORS && !this.issuesRequested) {
      this.loadIssues();
    }
  }

  loadIssues(): void {
    this.issuesRequested = true;
    this.issues$.next();
  }

  onIssueFilterChange(): void {
    this.issuePageIndex = 0;
    this.loadIssues();
  }

  onIssuePage(event: PageEvent): void {
    this.issuePageIndex = event.pageIndex;
    this.issuePageSize = event.pageSize;
    this.loadIssues();
  }

  showEntityErrors(entity: string): void {
    this.issueEntity = entity;
    this.issueKind = '';
    this.issuePageIndex = 0;
    this.selectedTab = TAB_ERRORS;
    this.loadIssues();
  }

  toggleIssue(issue: DataFeedIssue): void {
    if (this.expandedIssues.has(issue.IssueId)) {
      this.expandedIssues.delete(issue.IssueId);
    } else {
      this.expandedIssues.add(issue.IssueId);
    }
  }

  copyIssueRecord(issue: DataFeedIssue): void {
    this.copy(prettyJson(issue.Record) || issue.Message, 'Record');
  }

  copyIssuesOnPage(): void {
    const items = (this.issuePage?.Items ?? []).map(i => ({
      IssueId: i.IssueId,
      Entity: i.Entity,
      Kind: i.Kind,
      ClientKey: i.ClientKey,
      Message: i.Message,
      OccurredAt: i.OccurredAt,
      Record: safeParse(i.Record),
    }));
    this.copy(JSON.stringify(items, null, 2), `${items.length} errors`);
  }

  // ---------------------------------------------------------------------------
  // Timeline tab
  // ---------------------------------------------------------------------------

  toggleEvent(event: DataFeedEvent): void {
    if (this.expandedEvents.has(event.EventId)) {
      this.expandedEvents.delete(event.EventId);
    } else {
      this.expandedEvents.add(event.EventId);
    }
  }

  // ---------------------------------------------------------------------------

  copy(text: string | null | undefined, what: string): void {
    if (!text) { return; }
    const ok = this.clipboard.copy(text);
    this.snackBar.open(ok ? `${what} copied to clipboard` : `Could not copy ${what.toLowerCase()}`, 'Close', { duration: 2000 });
  }
}
